import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useLastEvent, useOrderSummary } from "@app/data";
import { useIsDropoffTracking } from "@app/shared/context/DropoffTrackingContext";
import { useOrderErrorRedirect } from "@app/shared/hooks/useOrderErrorRedirect";
import { orderPaths } from "@app/shared/routing/orderPaths";
import { backPathFromHome } from "@app/shared/routing/useOrderDetailsBackPath";
import { PageShell } from "@app/shared/layout";
import { EmptyState, Skeleton } from "@app/design-system/components";
import { RootState } from "@app/store";
import { t } from "@app/utils/translation";
import { TrackingHeader } from "./components/TrackingHeader";
import { MapView, MapProviderConfig } from "./components/Map";
import { BottomPanel } from "./components/BottomPanel";
import styles from "./HomePage.module.css";

/**
 * Entry point of the app. Once the order has loaded, this is a full-screen
 * map (`PageShell` `variant="map"`) with the header and bottom panel
 * floating over it — see PageShell.module.css for how that layering works.
 * Loading/error states use the normal scrolling `PageShell` instead, since
 * there's no map to show yet.
 */
export const HomePage = () => {
  const { trackCode = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const language = useSelector((state: RootState) => state.setting.language);

  // Mirrors the legacy `/d/:trackCode` route → `state.order.isDropoffTracking`:
  // a customer viewing this order through a drop-off-specific tracking link
  // rather than the main order link. Resolved once from the matched route
  // (the `/d/` prefix) via DropoffTrackingContext, exactly like the legacy
  // flag was resolved once from the matched route and read everywhere
  // downstream — see TrackingHeader.types.ts.
  const isDropoffTracking = useIsDropoffTracking();
  const { status, data: order, error } = useOrderSummary(trackCode, isDropoffTracking);

  // Unlike legacy (which redirected PickupDelivery orders straight to Order
  // Details, skipping Main entirely), every order kind lands on Home first
  // — a deliberate v2 product decision made after the initial redesign
  // pass, giving non-Ondemand orders a landing page too (see BottomPanel's
  // LastEventCard branch below). Only the -400/-403 error redirects apply
  // here, shared with Order Details via `useOrderErrorRedirect`.
  useOrderErrorRedirect();

  // Non-Ondemand orders show the order's last event instead of a driver
  // section (see BottomPanel) — skip the fetch entirely for Ondemand orders,
  // and while `order` hasn't loaded yet, mirroring `useDriverLocation`'s own
  // `enabled` gating in MapView.
  const isOnDemand = order?.kind === "ondemand";
  const { data: lastEvent } = useLastEvent(trackCode, !!order && !isOnDemand);

  // Sandbox-only: `?map=osm` or `?map=google` to preview the other map
  // provider against whichever scenario is loaded, without editing fixture
  // data — proves the provider switch is a config value, not a code change.
  // See Map/MapView.tsx.
  const mapParam = searchParams.get("map");
  const providerOverride: MapProviderConfig["kind"] | undefined =
    mapParam === "osm" || mapParam === "google" ? mapParam : undefined;

  if (status === "loading") {
    return (
      <PageShell>
        <div className={styles.loadingBody}>
          <Skeleton height={220} radius="var(--radius-lg)" />
          <Skeleton height={24} width="50%" />
          <Skeleton height={68} />
        </div>
      </PageShell>
    );
  }

  if (status === "error" || !order) {
    return (
      <PageShell>
        <div className={styles.loadingBody}>
          <EmptyState
            title={t("WeCouldntFindThisOrder", language)}
            description={error?.message ?? t("DoubleCheckTheLink", language)}
          />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      variant="map"
      topBar={
        <TrackingHeader
          logoUrl={order.logoUrl}
          orderCode={order.code}
          customerName={order.customer.name}
          isDropoffTracking={isDropoffTracking}
        />
      }
      bottomBar={
        <BottomPanel
          statusLabel={order.status.label}
          statusCode={order.status.code}
          statusTone={order.status.tone}
          orderKind={order.kind}
          driver={order.driver}
          lastEvent={lastEvent ?? null}
          isDropoffTracking={isDropoffTracking}
          dropoff={order.dropoffs[0] ?? null}
          remainingQueuePosition={order.remainingQueuePosition}
          onOrderDetailsClick={() => navigate(orderPaths.details(trackCode, isDropoffTracking))}
          onDropoffClick={() => {
            const dropoff = order.dropoffs[0];
            if (dropoff) {
              navigate(orderPaths.dropoff(trackCode, dropoff.id, isDropoffTracking), { state: backPathFromHome });
            }
          }}
        />
      }
    >
      <MapView order={order} isDropoffTracking={isDropoffTracking} providerOverride={providerOverride} />
    </PageShell>
  );
};
